import { Injectable, ViewContainerRef } from '@angular/core';

import { ProgressComponent } from '../progress/progress.component';
import { ToastFacade } from '../toast/toast.facade';

export type UiProgressPromiseOptions = {
  success?: {
    title: string;
    message: string;
  };
  error?: {
    title: string;
    message: string;
  };
  progress?: {
    container?: ViewContainerRef;
    color?: string;
  };
  errorHandler?: (error: Error) => Promise<void> | void;
};

@Injectable({
  providedIn: 'root',
})
export class UiProgressPromiseService {
  constructor(private readonly toastFacade: ToastFacade) { }

  async progress(container: ViewContainerRef, color?: string): Promise<{ hide: () => void; update: (percentage: number) => void }> {
    container.clear();
    const componentRef = container.createComponent(ProgressComponent);
    const hostElement = componentRef.location.nativeElement as HTMLElement;
    hostElement.classList.add('bg-white');
    hostElement.classList.add('rounded');
    hostElement.classList.add('pos-abs');

    const component = componentRef.instance;

    component.color = color || component.color;
    component.percentage = 0;

    const updatePercentage = (percentage: number) => {
      component.percentage = percentage;
    };

    return {
      hide: () => componentRef.destroy(),
      update: updatePercentage,
    };
  }

  async run<T>(
    func: (updateProgress?: (percentage: number) => void) => Promise<T>,
    opt: UiProgressPromiseOptions = {},
  ): Promise<{ error?: any; success?: T }> {
    let progress;
    if (opt.progress?.container) {
      const { container, color } = opt.progress;
      progress = await this.progress(container, color);
    }

    try {
      const success = await func(progress?.update);
      if (opt.success) {
        const { title, message } = opt.success;
        await this.toastFacade.present(message, title, { severity: 'success' , duration: 5000 });
      }
      return { success };
    } catch (error) {
      if (opt.errorHandler) {
        await opt.errorHandler(error as Error);
      }
      if (opt.error) {
        const { title: header, message } = opt.error;
        await this.toastFacade.present(message, header, { severity: 'error' , duration: 5000, mobileMode: 'persistent', browserMode: 'persistent'});
      }
      return {
        error,
      };
    } finally {
      if (opt.progress) {
        progress?.hide();
      }
    }
  }
}
